import { onMounted, onUnmounted, unref, watch } from 'vue'
import { buildKanbanWebSocketUrl } from '@/config/app'

const WS_HEARTBEAT_INTERVAL = 25000
const WS_PONG_TIMEOUT = 10000
const WS_INITIAL_RECONNECT_DELAY = 2000
const WS_MAX_RECONNECT_DELAY = 30000

/**
 * WebSocket do workspace SERVIÇOS (páginas de clientes):
 * - whatsapp.* → useWhatsAppChat / ClienteDetailModal
 * - cliente.chat.message → TaskChatPanel (histórico interno do cliente)
 */
export function useWorkspaceWhatsAppWebSocket(workspaceIdRef) {
  let ws = null
  let wsHeartbeatTimer = null
  let wsPongTimer = null
  let wsReconnectTimer = null
  let wsReconnectAttempts = 0
  let wsIntentionalClose = false
  let wsConnectGeneration = 0

  const stopHeartbeat = () => {
    if (wsHeartbeatTimer) {
      clearInterval(wsHeartbeatTimer)
      wsHeartbeatTimer = null
    }
    if (wsPongTimer) {
      clearTimeout(wsPongTimer)
      wsPongTimer = null
    }
  }

  const startHeartbeat = () => {
    stopHeartbeat()
    wsHeartbeatTimer = setInterval(() => {
      if (ws && ws.readyState === WebSocket.OPEN) {
        try {
          ws.send('ping')
          wsPongTimer = setTimeout(() => {
            if (ws) ws.close()
          }, WS_PONG_TIMEOUT)
        } catch {
          /* ignore */
        }
      }
    }, WS_HEARTBEAT_INTERVAL)
  }

  const disposeWebSocket = () => {
    if (!ws) return
    const old = ws
    ws = null
    old.onopen = null
    old.onclose = null
    old.onerror = null
    old.onmessage = null
    try {
      old.close(1000, 'replaced')
    } catch {
      /* ignore */
    }
  }

  const scheduleReconnect = () => {
    if (wsReconnectTimer || wsIntentionalClose) return
    const delay = Math.min(
      WS_INITIAL_RECONNECT_DELAY * Math.pow(2, wsReconnectAttempts),
      WS_MAX_RECONNECT_DELAY
    )
    wsReconnectTimer = setTimeout(() => {
      wsReconnectTimer = null
      wsReconnectAttempts++
      connect()
    }, delay)
  }

  const connect = () => {
    const workspaceId = unref(workspaceIdRef)
    const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null
    if (!workspaceId || !token) return

    disposeWebSocket()
    if (wsReconnectTimer) {
      clearTimeout(wsReconnectTimer)
      wsReconnectTimer = null
    }

    const generation = ++wsConnectGeneration
    const wsUrl = buildKanbanWebSocketUrl(workspaceId, token)
    if (!wsUrl) return

    wsIntentionalClose = false
    const socket = new WebSocket(wsUrl)
    ws = socket

    socket.onopen = () => {
      if (generation !== wsConnectGeneration || ws !== socket) return
      wsReconnectAttempts = 0
      startHeartbeat()
    }

    socket.onclose = () => {
      if (generation !== wsConnectGeneration) return
      stopHeartbeat()
      if (ws === socket) ws = null
      if (!wsIntentionalClose) scheduleReconnect()
    }

    socket.onerror = () => {
      /* onclose handles reconnect */
    }

    socket.onmessage = (evt) => {
      if (generation !== wsConnectGeneration || ws !== socket) return
      try {
        if (evt.data === 'pong') {
          if (wsPongTimer) {
            clearTimeout(wsPongTimer)
            wsPongTimer = null
          }
          return
        }
        const data = JSON.parse(evt.data || '{}')
        const type = data.type
        const payload = data.payload

        if (type && type.startsWith('whatsapp.')) {
          window.dispatchEvent(new CustomEvent(type, { detail: payload }))
          return
        }

        if (type === 'cliente.chat.message' && payload && payload.clienteId) {
          window.dispatchEvent(new CustomEvent('cliente.chat.message', { detail: payload }))
        }
      } catch (e) {
        console.warn('[WS WhatsApp] mensagem inválida:', e)
      }
    }
  }

  const disconnect = () => {
    wsIntentionalClose = true
    stopHeartbeat()
    if (wsReconnectTimer) {
      clearTimeout(wsReconnectTimer)
      wsReconnectTimer = null
    }
    disposeWebSocket()
  }

  onMounted(() => {
    connect()
    watch(
      () => unref(workspaceIdRef),
      (id, prev) => {
        if (id && id !== prev) {
          wsReconnectAttempts = 0
          connect()
        }
      }
    )
  })

  onUnmounted(() => {
    disconnect()
  })
}
